import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 15: SAMUNDAR - Flowing Grace
 * Style: Fluid, graceful, oceanic with deep blue accents on warm ivory
 * Best for: Chiffon Sarees, Silk Dupattas, Flowing Anarkalis, Drape Dresses
 * Deep blue (#1E40AF) on warm ivory (#FEFCE8)
 */
const Template15 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [hoveredCollection, setHoveredCollection] = useState(null);
    const touchStartX = useRef(0);

    // Collections
    const collections = [
        {
            name: 'Monsoon Edit',
            description: 'Lightweight chiffons and flowing silhouettes that dance with the rain.',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'Festive Flow',
            description: 'Rich silks and embroidered drapes for celebrations that shimmer.',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'Everyday Grace',
            description: 'Effortless elegance for the modern woman who moves with purpose.',
            image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
    ];

    // Categories
    const categories = [
        { name: 'Chiffon Sarees', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Silk Dupattas', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Flowing Anarkalis', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Drape Dresses', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Products
    const products = [
        { id: 1, name: 'Ocean Breeze Chiffon Saree', price: '\u20B97,999', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400', badge: 'New' },
        { id: 2, name: 'Silk Cascade Dupatta', price: '\u20B93,499', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Flowing Teal Anarkali', price: '\u20B98,999', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Ivory Drape Dress', price: '\u20B96,499', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400', badge: 'Trending' },
        { id: 5, name: 'Sapphire Georgette Saree', price: '\u20B911,999', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Pearl White Silk Dupatta', price: '\u20B94,999', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Midnight Blue Anarkali Set', price: '\u20B914,999', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400', badge: 'Bestseller' },
        { id: 8, name: 'Wave Pattern Drape Gown', price: '\u20B91,999', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The chiffon saree I ordered drapes like a dream. The fabric flows so beautifully, it feels like wearing a gentle ocean wave. Absolutely stunning craftsmanship.',
            name: 'Meera Sharma',
            city: 'Mumbai',
            rating: 5,
        },
        {
            text: 'SAMUNDAR understands flowing fabrics like no other brand. My silk dupatta is the most graceful piece in my wardrobe. Every compliment I receive is well-deserved by this team.',
            name: 'Ananya Iyer',
            city: 'Chennai',
            rating: 5,
        },
        {
            text: 'I wore the flowing anarkali to my cousin\'s wedding and received endless compliments. The deep blue colour with the ivory embroidery is simply divine. Will order again!',
            name: 'Ritu Kapoor',
            city: 'Delhi',
            rating: 5,
        },
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div style={{ fontFamily: "'Nunito Sans', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&family=Nunito+Sans:wght@300;400;600&display=swap');
                @keyframes waveFloat {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-8px); }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>

            {/* ===== SECTION 1: WAVE HERO ===== */}
            <section className="relative w-full overflow-hidden" style={{ backgroundColor: '#FEFCE8' }}>
                {/* Hero Image */}
                <div className="relative w-full" style={{ minHeight: '90vh' }}>
                    <img
                        src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200"
                        alt="Flowing Elegance"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    {/* Overlay */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background: 'linear-gradient(to bottom, rgba(30,64,175,0.15) 0%, rgba(30,64,175,0.35) 50%, rgba(254,252,232,0.95) 85%, #FEFCE8 100%)',
                        }}
                    />

                    {/* Hero Content */}
                    <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 sm:px-6" style={{ minHeight: '90vh', paddingTop: '72px' }}>
                        <p
                            className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-4"
                            style={{ color: '#FEFCE8', fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600 }}
                        >
                            Graceful Indian Fashion
                        </p>
                        <h1
                            className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-4 tracking-wide"
                            style={{
                                fontFamily: "'Quicksand', sans-serif",
                                color: '#FEFCE8',
                                textShadow: '0 2px 20px rgba(30, 64, 175, 0.3)',
                            }}
                        >
                            Flowing Elegance
                        </h1>
                        <p
                            className="text-base sm:text-lg font-light mb-8 max-w-md mx-auto"
                            style={{
                                color: 'rgba(254, 252, 232, 0.9)',
                                fontFamily: "'Nunito Sans', sans-serif",
                            }}
                        >
                            Where fabric meets the fluidity of the ocean
                        </p>
                        <button
                            onClick={() => navigate('/template15/shop')}
                            className="inline-flex items-center gap-3 px-8 py-3.5 sm:px-10 sm:py-4 text-sm tracking-wide uppercase transition-all duration-300"
                            style={{
                                backgroundColor: '#1E40AF',
                                border: '2px solid #1E40AF',
                                color: '#FFFFFF',
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontWeight: 600,
                                borderRadius: '9999px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#1E3A8A';
                                e.currentTarget.style.borderColor = '#1E3A8A';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#1E40AF';
                                e.currentTarget.style.borderColor = '#1E40AF';
                            }}
                        >
                            Explore Collection <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Wave SVG bottom */}
                <div className="absolute bottom-0 left-0 right-0" style={{ zIndex: 20 }}>
                    <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" style={{ display: 'block' }}>
                        <path d="M0,60 C240,120 480,0 720,60 C960,120 1200,0 1440,60 L1440,120 L0,120 Z" fill="#FEFCE8" />
                    </svg>
                </div>
            </section>

            {/* ===== SECTION 2: COLLECTIONS ===== */}
            <section style={{ backgroundColor: '#FEFCE8', padding: '60px 0 80px' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
                    {/* Section Header */}
                    <div className="text-center mb-12">
                        <p
                            style={{
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#1E40AF',
                                letterSpacing: '3px',
                                textTransform: 'uppercase',
                                marginBottom: '8px',
                            }}
                        >
                            Curated For You
                        </p>
                        <h2
                            style={{
                                fontFamily: "'Quicksand', sans-serif",
                                fontSize: 'clamp(28px, 5vw, 44px)',
                                fontWeight: 700,
                                color: '#1E293B',
                                marginBottom: '12px',
                            }}
                        >
                            Our Collections
                        </h2>
                        <p style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: '16px', color: '#64748B', maxWidth: '500px', margin: '0 auto', fontWeight: 300 }}>
                            Each collection tells a story of movement, grace, and timeless beauty
                        </p>
                    </div>

                    {/* Collection Cards */}
                    <div className="samundar-collections-grid">
                        {collections.map((col, i) => (
                            <div
                                key={i}
                                onClick={() => navigate('/template15/shop')}
                                onMouseEnter={() => setHoveredCollection(i)}
                                onMouseLeave={() => setHoveredCollection(null)}
                                style={{
                                    position: 'relative',
                                    borderRadius: '20px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    aspectRatio: '4 / 5',
                                    transition: 'transform 0.4s ease, box-shadow 0.4s ease',
                                    transform: hoveredCollection === i ? 'translateY(-6px)' : 'translateY(0)',
                                    boxShadow: hoveredCollection === i
                                        ? '0 16px 40px rgba(30, 64, 175, 0.15)'
                                        : '0 4px 12px rgba(30, 64, 175, 0.06)',
                                }}
                            >
                                <img
                                    src={col.image}
                                    alt={col.name}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        transform: hoveredCollection === i ? 'scale(1.05)' : 'scale(1)',
                                        transition: 'transform 0.6s ease',
                                    }}
                                />
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'linear-gradient(to top, rgba(30, 64, 175, 0.85) 0%, rgba(30, 64, 175, 0.2) 50%, transparent 100%)',
                                    }}
                                />
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '24px' }}>
                                    <h3
                                        style={{
                                            fontFamily: "'Quicksand', sans-serif",
                                            fontSize: '22px',
                                            fontWeight: 700,
                                            color: '#FEFCE8',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        {col.name}
                                    </h3>
                                    <p
                                        style={{
                                            fontFamily: "'Nunito Sans', sans-serif",
                                            fontSize: '14px',
                                            color: 'rgba(254, 252, 232, 0.8)',
                                            lineHeight: 1.5,
                                            fontWeight: 300,
                                        }}
                                    >
                                        {col.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: CATEGORIES ===== */}
            <section style={{ backgroundColor: '#FEF9C3', padding: '80px 0' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
                    <div className="text-center mb-12">
                        <p style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: '13px', fontWeight: 600, color: '#1E40AF', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '8px' }}>
                            Browse By Style
                        </p>
                        <h2 style={{ fontFamily: "'Quicksand', sans-serif", fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: 700, color: '#1E293B' }}>
                            Shop Categories
                        </h2>
                    </div>

                    <div className="samundar-categories-grid">
                        {categories.map((cat, i) => (
                            <div
                                key={i}
                                onClick={() => navigate('/template15/shop')}
                                style={{
                                    position: 'relative',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    aspectRatio: '1',
                                    transition: 'transform 0.3s ease',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <img
                                    src={cat.image}
                                    alt={cat.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(30, 64, 175, 0.75) 0%, transparent 60%)' }} />
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px', textAlign: 'center' }}>
                                    <h3
                                        style={{
                                            fontFamily: "'Quicksand', sans-serif",
                                            fontSize: '18px',
                                            fontWeight: 700,
                                            color: '#FEFCE8',
                                        }}
                                    >
                                        {cat.name}
                                    </h3>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: PRODUCTS ===== */}
            <section style={{ backgroundColor: '#FEFCE8', padding: '80px 0' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
                    <div className="text-center mb-12">
                        <p style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: '13px', fontWeight: 600, color: '#1E40AF', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '8px' }}>
                            Handpicked Pieces
                        </p>
                        <h2 style={{ fontFamily: "'Quicksand', sans-serif", fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: 700, color: '#1E293B' }}>
                            Featured Collection
                        </h2>
                    </div>

                    <div className="samundar-products-grid">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template15/product/${product.id}`)}
                                onMouseEnter={() => setHoveredProduct(product.id)}
                                onMouseLeave={() => setHoveredProduct(null)}
                                style={{
                                    backgroundColor: '#FEFCE8',
                                    borderRadius: '16px',
                                    cursor: 'pointer',
                                    transition: 'all 0.4s ease',
                                    overflow: 'hidden',
                                    boxShadow: hoveredProduct === product.id
                                        ? '0 12px 32px rgba(30, 64, 175, 0.12)'
                                        : '0 2px 8px rgba(30, 64, 175, 0.06)',
                                    transform: hoveredProduct === product.id ? 'translateY(-4px)' : 'translateY(0)',
                                }}
                            >
                                <div style={{ position: 'relative', aspectRatio: '3 / 4', overflow: 'hidden', borderRadius: '16px 16px 0 0' }}>
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transform: hoveredProduct === product.id ? 'scale(1.05)' : 'scale(1)',
                                            transition: 'transform 0.5s ease',
                                        }}
                                    />
                                    {product.badge && (
                                        <span style={{
                                            position: 'absolute', top: '12px', left: '12px',
                                            backgroundColor: '#1E40AF', color: '#FFFFFF',
                                            fontFamily: "'Nunito Sans', sans-serif", fontSize: '11px', fontWeight: 600,
                                            padding: '4px 14px', borderRadius: '9999px', textTransform: 'uppercase',
                                        }}>
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        style={{
                                            position: 'absolute', top: '12px', right: '12px',
                                            width: '36px', height: '36px',
                                            backgroundColor: 'rgba(254, 252, 232, 0.85)',
                                            border: 'none', borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer', backdropFilter: 'blur(4px)',
                                        }}
                                    >
                                        <Heart
                                            size={18}
                                            style={{
                                                color: wishlist[product.id] ? '#1E40AF' : '#64748B',
                                                fill: wishlist[product.id] ? '#1E40AF' : 'none',
                                            }}
                                        />
                                    </button>
                                </div>
                                <div style={{ padding: '16px' }}>
                                    <h3 style={{
                                        fontFamily: "'Quicksand', sans-serif", fontSize: '15px', fontWeight: 600,
                                        color: '#1E293B', margin: '0 0 8px 0', overflow: 'hidden',
                                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {product.name}
                                    </h3>
                                    <span style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: '16px', fontWeight: 600, color: '#1E40AF' }}>
                                        {product.price}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* View All Button */}
                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/template15/shop')}
                            style={{
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontSize: '14px',
                                fontWeight: 600,
                                padding: '14px 40px',
                                backgroundColor: 'transparent',
                                color: '#1E40AF',
                                border: '2px solid #1E40AF',
                                borderRadius: '9999px',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#1E40AF';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#1E40AF';
                            }}
                        >
                            View All Products
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: TESTIMONIALS ===== */}
            <section style={{ backgroundColor: '#FEF9C3', padding: '80px 0' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px' }}>
                    <div className="text-center mb-12">
                        <p style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: '13px', fontWeight: 600, color: '#1E40AF', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '8px' }}>
                            What They Say
                        </p>
                        <h2 style={{ fontFamily: "'Quicksand', sans-serif", fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 700, color: '#1E293B' }}>
                            Customer Stories
                        </h2>
                    </div>

                    <div
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        style={{ textAlign: 'center', position: 'relative' }}
                    >
                        {/* Stars */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '20px' }}>
                            {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                <Star key={i} size={18} style={{ color: '#1E40AF', fill: '#1E40AF' }} />
                            ))}
                        </div>

                        {/* Quote */}
                        <p
                            style={{
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontSize: '18px',
                                fontWeight: 300,
                                color: '#1E293B',
                                lineHeight: 1.8,
                                marginBottom: '24px',
                                fontStyle: 'italic',
                                minHeight: '100px',
                            }}
                        >
                            "{testimonials[activeTestimonial].text}"
                        </p>

                        {/* Author */}
                        <p style={{ fontFamily: "'Quicksand', sans-serif", fontSize: '16px', fontWeight: 700, color: '#1E40AF', marginBottom: '4px' }}>
                            {testimonials[activeTestimonial].name}
                        </p>
                        <p style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: '14px', color: '#64748B' }}>
                            {testimonials[activeTestimonial].city}
                        </p>

                        {/* Dots */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '32px' }}>
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    style={{
                                        width: activeTestimonial === i ? '32px' : '10px',
                                        height: '10px',
                                        borderRadius: '9999px',
                                        backgroundColor: activeTestimonial === i ? '#1E40AF' : 'rgba(30, 64, 175, 0.2)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: NEWSLETTER ON BLUE BG ===== */}
            <section style={{ backgroundColor: '#1E40AF', padding: '80px 0' }}>
                <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
                    <h2 style={{
                        fontFamily: "'Quicksand', sans-serif", fontSize: 'clamp(24px, 4vw, 36px)',
                        fontWeight: 700, color: '#FEFCE8', marginBottom: '12px',
                    }}>
                        Stay in the Flow
                    </h2>
                    <p style={{
                        fontFamily: "'Nunito Sans', sans-serif", fontSize: '15px',
                        color: 'rgba(254, 252, 232, 0.75)', marginBottom: '32px', fontWeight: 300,
                    }}>
                        Subscribe for exclusive collections, early access, and style inspirations delivered to your inbox.
                    </p>
                    <form
                        onSubmit={(e) => { e.preventDefault(); if (email.trim()) setEmail(''); }}
                        className="samundar-newsletter-form"
                        style={{ display: 'flex', gap: '0', maxWidth: '480px', margin: '0 auto' }}
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                            style={{
                                flex: 1,
                                padding: '14px 20px',
                                backgroundColor: 'rgba(254, 252, 232, 0.15)',
                                border: '1px solid rgba(254, 252, 232, 0.3)',
                                borderRight: 'none',
                                borderRadius: '9999px 0 0 9999px',
                                color: '#FEFCE8',
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontSize: '14px',
                                outline: 'none',
                            }}
                        />
                        <button
                            type="submit"
                            style={{
                                padding: '14px 28px',
                                backgroundColor: '#FEFCE8',
                                color: '#1E40AF',
                                border: 'none',
                                borderRadius: '0 9999px 9999px 0',
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'background-color 0.3s ease',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                            }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#FEF9C3')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = '#FEFCE8')}
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
            </section>

            {/* Responsive Styles */}
            <style>{`
                .samundar-collections-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 24px;
                }
                .samundar-categories-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px;
                }
                .samundar-products-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px;
                }
                @media (min-width: 768px) {
                    .samundar-collections-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 28px;
                    }
                    .samundar-categories-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 24px;
                    }
                    .samundar-products-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 20px;
                    }
                }
                @media (min-width: 1024px) {
                    .samundar-products-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 24px;
                    }
                }
                @media (max-width: 768px) {
                    .samundar-newsletter-form {
                        flex-direction: column !important;
                        gap: 12px !important;
                    }
                    .samundar-newsletter-form input {
                        border-right: 1px solid rgba(254, 252, 232, 0.3) !important;
                        border-radius: 9999px !important;
                    }
                    .samundar-newsletter-form button {
                        border-radius: 9999px !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default Template15;
